if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 768px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (event.detail && event.detail.currentElement) {
          const currentElement = event.detail.currentElement;
          if (currentElement.dataset && currentElement.dataset.mediaId) {
            // Prevent resetting to the first image if a variant was selected
            if (this.elements.viewer.dataset.selectedMediaId && 
                this.elements.viewer.dataset.selectedMediaId !== currentElement.dataset.mediaId) {
                return;
            }
            const thumbnail = this.elements.thumbnails.querySelector(
                `[data-target="${currentElement.dataset.mediaId}"]`
            );
            this.setActiveThumbnail(thumbnail);
          }
        }
      }
      
      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);
        }
        this.elements.viewer.dataset.selectedMediaId = mediaId;
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // Get the thumbnail slider element
        const thumbnailSlider = this.elements.thumbnails.querySelector('.slider');
        if (!thumbnailSlider) return;
        
        // Check if we need to scroll (thumbnail is not visible)
        if (!this.isThumbnailVisible(thumbnail, thumbnailSlider)) {
          this.scrollToThumbnail(thumbnail, thumbnailSlider);
        }
      }

      // Check if thumbnail is visible in the viewport
      isThumbnailVisible(thumbnail, slider) {
        if (!thumbnail || !slider) return false;
        
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const sliderRect = slider.getBoundingClientRect();
        
        // Check if thumbnail is fully visible within the slider container
        // Using 5px tolerance for partial visibility
        const tolerance = 5;
        
        // Determine scroll direction based on actual layout
        const isVerticalLayout = this.isVerticalLayout();
        
        if (isVerticalLayout) {
          // For vertical layout
          return (
            thumbnailRect.top >= sliderRect.top - tolerance &&
            thumbnailRect.bottom <= sliderRect.bottom + tolerance
          );
        } else {
          // For horizontal layout (default for mobile)
          return (
            thumbnailRect.left >= sliderRect.left - tolerance &&
            thumbnailRect.right <= sliderRect.right + tolerance
          );
        }
      }

      // Check if this is a vertical slider layout
      isVerticalLayout() {
        // Get the thumbnail slider container
        const thumbnailSlider = this.elements.thumbnails.querySelector('.slider');
        if (!thumbnailSlider) return false;
        
        // Check actual computed styles to determine layout
        const computedStyle = window.getComputedStyle(thumbnailSlider);
        const flexDirection = computedStyle.flexDirection;
        
        // If flex-direction is column, it's vertical
        if (flexDirection === 'column') {
          return true;
        }
        
        // Check for vertical scrolling behavior
        const overflowY = computedStyle.overflowY;
        const overflowX = computedStyle.overflowX;
        
        // If overflow-y is auto/scroll and overflow-x is hidden, it's likely vertical
        if ((overflowY === 'auto' || overflowY === 'scroll') && overflowX === 'hidden') {
          return true;
        }
        
        // Check parent classes for vertical layout indicators
        const parentElement = this.elements.thumbnails.parentElement;
        if (parentElement && parentElement.classList.contains('thumb_leftright')) {
          // For desktop left/right slider layouts
          return this.mql.matches; // Only vertical on desktop
        }
        
        // Default to horizontal
        return false;
      }

      // Scroll to make thumbnail visible
      scrollToThumbnail(thumbnail, slider) {
        if (!thumbnail || !slider) return;
        
        const isVertical = this.isVerticalLayout();
        const slideIndex = Array.from(slider.children).indexOf(thumbnail);
        
        if (slideIndex < 0) return; // Thumbnail not found in slider
        
        if (isVertical) {
          // For vertical sliders
          const scrollPosition = slideIndex * thumbnail.offsetHeight;
          
          // Smooth scroll to the thumbnail
          slider.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        } else {
          // For horizontal sliders
          const scrollPosition = slideIndex * thumbnail.offsetWidth;
          
          // Smooth scroll to the thumbnail
          slider.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}